import React, {useCallback, useMemo, memo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {MoreVertical} from 'lucide-react';

import {GUEST_STATUSES, ShortGuest, useTimeoutRef} from '@chekin/core';
import {useAuth} from 'hooks/auth/useAuth';
import {useDeleteGuest} from 'hooks/useDeleteGuest';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {usePath} from 'hooks';
import {appendLeaderParam} from 'utils/guestForm';
import {GuestBox, useModalControls} from '@chekin/guestapp-ui-kit';
import {PATHS} from 'routes/constants';
import {useErrorModal, useIVStore} from '@chekin/shared';

import Dropdown from 'components/common/Dropdown';
import {GuestStatusesTexts} from 'components/GuestsPanel/types';
import {DeleteGuestStatusModal} from './DeteleGuestStatusModal';
import ChekinProSettings from 'ChekinProSettings';

const guestStatusText: GuestStatusesTexts = {
  [GUEST_STATUSES.complete]: 'completed',
  [GUEST_STATUSES.verification_pending]: 'verification_pending',
  [GUEST_STATUSES.incomplete]: 'registration_pending',
};

const DELETE_GUEST_MODAL_TIMEOUT = 3000;

type Props = {
  guestMember: ShortGuest & {isLeader?: boolean};
  isLoading?: boolean;
  onRegisterNew?: () => void;
  disabled?: boolean;
};
const GuestItem = memo(({guestMember, isLoading, onRegisterNew, disabled}: Props) => {
  const {getMainPath} = usePath();
  const {resetAllIVState} = useIVStore();
  const {ErrorModal, displayError} = useErrorModal();
  const navigate = useNavigate();
  const {isLoggedIn} = useAuth();
  const closeModalTimeoutRef = useTimeoutRef();
  const {isOpen, openModal, closeModal} = useModalControls();
  const {t} = useTranslation();
  const {mutate: deleteGuestMutate, status: deleteGuestStatus} = useDeleteGuest({
    onError: displayError,
    onMutate: openModal,
    onSuccess: () => {
      closeModalTimeoutRef.current = setTimeout(() => {
        closeModal();
      }, DELETE_GUEST_MODAL_TIMEOUT);
    },
  });

  const {id: guestId, full_name, guestapp_status, isLeader} = guestMember;
  const completed = guestapp_status === GUEST_STATUSES.complete;
  const statusText = guestStatusText[guestapp_status] || '';
  const isEditable = !completed;

  const {isIVOnPropertyAvailable} = useIVOnProperty();
  const getLinkToUpdateGuestProcessView = useCallback(
    (guest?: ShortGuest) => {
      const guestId = guest?.id;
      const guestStatus = guest?.guestapp_status;
      const isVerificationPending = guestStatus === GUEST_STATUSES.verification_pending;
      const isPreRegisteredAndInCompleted =
        guest?.is_pre_registered && guestStatus === GUEST_STATUSES.incomplete;
      if (guestId) {
        if (isVerificationPending) {
          return isIVOnPropertyAvailable
            ? getMainPath(
                `${PATHS.verification.main}/${guestId}/${PATHS.verification.qrScan}`,
              )
            : getMainPath(`${PATHS.verification.main}/${guestId}`);
        }
        if (isPreRegisteredAndInCompleted || isLoggedIn) {
          return appendLeaderParam(
            getMainPath(`${PATHS.addPersonalDataForm}/${guestId}`),
            isLeader,
          );
        }
      }
    },
    [getMainPath, isIVOnPropertyAvailable, isLeader, isLoggedIn],
  );

  const gotToEdit = useCallback(() => {
    const link = getLinkToUpdateGuestProcessView(guestMember);

    if (isEditable && link) {
      resetAllIVState();
      navigate(link);
    }
  }, [
    getLinkToUpdateGuestProcessView,
    guestMember,
    isEditable,
    navigate,
    resetAllIVState,
  ]);

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      if ((isEditable || ChekinProSettings.enableGuestsRemoval) && guestMember.id) {
        deleteGuestMutate(guestMember.id);
      }
    },
    [deleteGuestMutate, isEditable, guestMember.id],
  );

  const handleGuestClick = useCallback(() => {
    if (disabled) return;
    if (full_name) {
      gotToEdit();
      return;
    }
    onRegisterNew?.();
  }, [disabled, full_name, gotToEdit, onRegisterNew]);

  const dropdownOptions = useMemo(() => {
    return [
      // {
      //   id: 1,
      //   label: t('fill_in_incomplete_data'),
      //   onClick: () => {
      //     console.log('remove item');
      //   },
      // },
      {
        id: 2,
        label: t('edit_guest'),
        onClick: gotToEdit,
        hidden: !isEditable,
      },
      {
        id: 3,
        label: t('delete_guest'),
        onClick: handleDelete,
        hidden: !(isEditable || ChekinProSettings.enableGuestsRemoval),
      },
    ];
  }, [gotToEdit, handleDelete, isEditable, t]);

  const showActions = dropdownOptions.some(action => !action.hidden);

  return (
    <>
      <GuestBox
        hasGuest={!!guestId}
        guestName={full_name || t('unknown_guest')}
        completed={completed}
        loading={isLoading}
        status={t(statusText)}
        showAddBtn={!guestId && !disabled}
        onClick={handleGuestClick}
        className={disabled ? '!cursor-not-allowed pointer-events-none' : undefined}
        actionsButton={
          !!guestId && showActions && !disabled ? (
            <Dropdown
              position="bottom right"
              options={dropdownOptions}
              disabled={isLoading}
              trigger={
                <div className="flex items-center justify-center text-primary">
                  <MoreVertical className="w-6 h-6 kiosko:w-10 kiosko:h-10 " />
                </div>
              }
            />
          ) : undefined
        }
      />

      <ErrorModal />
      <DeleteGuestStatusModal open={isOpen} state={deleteGuestStatus} />
    </>
  );
});

export {GuestItem};
